/**
 * Helpers for matching features by similar names (bv6 / bvLook)
 */

// shower size words in feature names: "30", "30in", "48", "48in", "60", "60in" (can be on any position)
const SHOWER_SIZE_WORD = /^(30|48|60)(in)?$/i;

const splitToWords = (name = ""): string[] => (name || "").split(/\s+/).filter(Boolean);

/**
 * Removes shower size words from the name, e.g.
 * "30 tibur stone 1 american standard" and "tibur stone 60in 1 american standard flat"
 * -> "tibur stone 1 american standard" and "tibur stone 1 american standard flat"
 */
export const removeShowerSizeFromName = (name = ""): string => {
  return splitToWords(name).filter(word => !SHOWER_SIZE_WORD.test(word)).join(" ");
};

/**
 * Returns the cached no size name for the feature, recalculates it only if the original name was changed
 * @param feature {object} - the cache is stored on it
 * @param cacheKey {string}
 * @param name {string} - original name
 */
export const getCachedNoSizeName = (feature: any, cacheKey: string, name = ""): string => {
  const cached = feature[cacheKey];
  if (!cached || cached.from !== name) {
    feature[cacheKey] = {from: name, to: removeShowerSizeFromName(name)};
  }
  return feature[cacheKey].to
};

/**
 * Finds the item which name shares the most words with the name (at least half of the name words must match).
 * If several items have the same amount of matching words - the one with less words (closer name) wins.
 * e.g. "osage birch eggshell fm5830 1 bertch" -> "osage birch eggshell fm2230 1 bertch"
 * @param name {string}
 * @param items {array}
 * @param getItemName {function} - returns the name to compare for the item
 */
export const findItemWithMostSameWords = <T>(name: string, items: T[] = [], getItemName: (item: T) => string): T | undefined => {
  const nameWords = new Set(splitToWords(name));
  if (!nameWords.size) {
    return undefined
  }
  const minSameWords = Math.ceil(nameWords.size / 2); // at least half of the words must match
  let foundItem: T | undefined;
  let maxSameWords = 0;
  let foundItemWordsCount = 0;
  items.forEach(item => {
    const itemWords = splitToWords(getItemName(item));
    const sameWords = itemWords.filter(word => nameWords.has(word)).length;
    if (sameWords < minSameWords) {
      return
    }
    if (sameWords > maxSameWords || (sameWords === maxSameWords && itemWords.length < foundItemWordsCount)) {
      maxSameWords = sameWords;
      foundItemWordsCount = itemWords.length;
      foundItem = item;
    }
  });
  return foundItem
};
